import { useCallback, useEffect, useRef, useState } from "react";
import { useWorkspace } from "@/context/WorkspaceContext";
import { api, apiError } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import RecordingControls from "@/components/RecordingControls";
import LiveRoomPanel from "@/components/LiveRoomPanel";

export default function Communications({ kind }) {
  const { projectId } = useWorkspace();
  const [items, setItems] = useState([]), [total, setTotal] = useState(0), [page, setPage] = useState(1);
  const [title, setTitle] = useState(""), [emails, setEmails] = useState(""), [scheduled, setScheduled] = useState("");
  const [selected, setSelected] = useState(null), [identity, setIdentity] = useState("");
  const [manage, setManage] = useState(false), [busy, setBusy] = useState(false), [error, setError] = useState("");
  const request = useRef(0);
  const [mediaSession, setMediaSession] = useState(null);
  const scope = useRef("");
  scope.current = `${projectId}:${kind}`;
  useEffect(() => { scope.current = `${projectId}:${kind}`; return () => { scope.current = ""; }; }, [projectId, kind]);
  const load = useCallback(async () => {
    if (!projectId) return;
    const version = ++request.current;
    try {
      const { data } = await api.get(`/dashboard/projects/${projectId}/${kind}`, { params: { page } });
      if (version !== request.current) return;
      setItems(data.items); setTotal(data.total); setManage(data.can_manage); setIdentity(data.identity || "");
    } catch (e) { if (version === request.current) setError(apiError(e)); }
  }, [projectId, kind, page]);
  useEffect(() => { setSelected(null); setItems([]); setError(""); setManage(false); setPage(1); }, [projectId, kind]);
  useEffect(() => { load(); const timer = setInterval(load, 5000); return () => {
    clearInterval(timer);
    // Invalidate any in-flight list request when the project or page changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    request.current++;
  }; }, [load]);
  const create = async (e) => {
    e.preventDefault(); if (!projectId || busy || !manage) return;
    const context = scope.current;
    setBusy(true); setError("");
    try {
      const body = kind === "calls" ? { callee_emails: emails.split(/[;,\n]/).map((v) => v.trim()).filter(Boolean), type: "video" } : { title, ...(scheduled ? { scheduled_at: new Date(scheduled).toISOString() } : {}) };
      const { data } = await api.post(`/dashboard/projects/${projectId}/${kind}`, body);
      if (context !== scope.current) return;
      setTitle(""); setEmails(""); setScheduled("");
      if (kind === "calls") setSelected(data.call); else if (data.status === "live") setSelected(data);
      await load();
    } catch (err) { if (context === scope.current) setError(apiError(err)); } finally { setBusy(false); }
  };
  const action = async (item, command) => {
    if (busy) return;
    const context = scope.current;
    setBusy(true); setError("");
    try {
      await api.post(`/dashboard/${kind}/${item.id}/${command}`);
      if (context !== scope.current) return;
      if (command === "accept" || command === "start") setSelected(item);
      if (["end", "cancel", "decline"].includes(command) && selected?.id === item.id) setSelected(null);
      await load();
    } catch (err) { if (context === scope.current) setError(apiError(err)); } finally { setBusy(false); }
  };
  if (!projectId) return <p>Sélectionnez un projet pour continuer.</p>;
  return <div className="space-y-5">
    <h1 className="text-2xl font-semibold">{kind === "calls" ? "Appels" : "Réunions"}</h1>
    {error && <p role="alert" className="text-red-400">{error}</p>}
    {manage && <form onSubmit={create} className="space-y-3 rounded border border-zinc-800 p-4">
      {kind === "calls" ? <label className="block space-y-1 text-sm">Inviter des membres du projet (emails séparés par une virgule)<Input required value={emails} onChange={(e) => setEmails(e.target.value)} placeholder="alice@example.com, bob@example.com" /></label> : <>
        <label className="block space-y-1 text-sm">Titre<Input required maxLength={160} value={title} onChange={(e) => setTitle(e.target.value)} /></label>
        <label className="block space-y-1 text-sm">Date locale (vide pour une réunion immédiate)<Input type="datetime-local" value={scheduled} onChange={(e) => setScheduled(e.target.value)} /></label>
      </>}
      <Button disabled={busy || (kind === "calls" ? !emails.trim() : !title.trim())}>{kind === "calls" ? "Appeler" : "Créer la réunion"}</Button>
    </form>}
    {selected && <LiveRoomPanel key={`${kind}-${selected.id}`} roomId={selected.id} tokenEndpoint={`/dashboard/${kind}/${selected.id}/token`} onSession={setMediaSession} />}
    {selected && <RecordingControls key={`${kind}-recording-${selected.id}`} kind={kind} resourceId={selected.id} mediaSession={mediaSession} />}
    <ul className="space-y-3">{items.map((item) => <li key={item.id} className="space-y-2 rounded border border-zinc-800 p-4">
      <div className="flex justify-between gap-3"><span>{item.title || item.caller_name || "Appel"}</span><span className="text-xs text-zinc-400">{item.status}</span></div>
      {item.scheduled_at && <p className="text-xs text-zinc-500">{new Date(item.scheduled_at).toLocaleString()}</p>}
      <div className="flex flex-wrap gap-2">
        {kind === "calls" && item.status === "ringing" && item.callees?.some((c) => c.identity === identity && c.status === "ringing") && <><Button disabled={busy} onClick={() => action(item, "accept")}>Accepter</Button><Button variant="outline" disabled={busy} onClick={() => action(item, "decline")}>Décliner</Button></>}
        {kind === "meetings" && item.status === "scheduled" && manage && <Button disabled={busy} onClick={() => action(item, "start")}>Démarrer</Button>}
        {((kind === "meetings" && item.status === "live") || (kind === "calls" && item.status === "ongoing" && (item.caller === identity || item.callees?.some((c) => c.identity === identity && c.status === "accepted")))) && <Button variant="outline" disabled={busy} onClick={() => setSelected(item)}>Ouvrir la connexion</Button>}
        {manage && ["live", "ongoing", "ringing"].includes(item.status) && <Button variant="outline" disabled={busy} onClick={() => action(item, item.status === "ringing" ? "cancel" : "end")}>Terminer</Button>}
      </div>
    </li>)}</ul>
    {!items.length && <p className="text-sm text-zinc-400">Aucun élément pour ce projet.</p>}
    <div className="flex gap-3"><Button variant="outline" disabled={page === 1 || busy} onClick={() => setPage(page-1)}>Précédent</Button><span className="text-sm">Page {page} · {total} élément(s)</span><Button variant="outline" disabled={page*25 >= total || busy} onClick={() => setPage(page+1)}>Suivant</Button></div>
  </div>;
}
